// LiqScoreHomeCard — large public-facing LiqScore widget for the landing
// page. Renders BTC as the protagonist with ETH + SOL on a compact row,
// updates every 60s, links into /liquidity-map.
//
// Public endpoint — works for guests, no auth.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";

const COINS = ["BTC", "ETH", "SOL"];
const FETCH_TIMEOUT_MS = 10_000;

const BAND_LABELS = {
  es: { cascade: "CASCADA INMINENTE", crit: "PRESIÓN CRÍTICA", high: "PRESIÓN ALTA", mod: "PRESIÓN MODERADA", low: "PRESIÓN BAJA" },
  en: { cascade: "IMMINENT CASCADE", crit: "CRITICAL PRESSURE", high: "HIGH PRESSURE", mod: "MODERATE PRESSURE", low: "LOW PRESSURE" },
  sk: { cascade: "BLÍŽIACA SA KASKÁDA", crit: "KRITICKÝ TLAK", high: "VYSOKÝ TLAK", mod: "MIERNY TLAK", low: "NÍZKY TLAK" },
  de: { cascade: "UNMITTELBARE KASKADE", crit: "KRITISCHER DRUCK", high: "HOHER DRUCK", mod: "MODERATER DRUCK", low: "GERINGER DRUCK" },
  "zh-CN": { cascade: "连环爆仓即将发生", crit: "临界压力", high: "高压力", mod: "中等压力", low: "低压力" },
};

const CARD_TXT = {
  es: { subtitle: "Índice global de presión de liquidaciones · BTC, ETH y SOL", cta: "Ver Liquidity Map completo", basis: "Basado en datos de 27 exchanges · DeFi · Ballenas", updated: "actualizado hace", loading_short: "cargando…", no_data: "Sin datos" },
  en: { subtitle: "Global liquidation-pressure index · BTC, ETH and SOL", cta: "See full Liquidity Map", basis: "Based on data from 27 exchanges · DeFi · Whales", updated: "updated", loading_short: "loading…", no_data: "No data" },
  sk: { subtitle: "Globálny index tlaku likvidácií · BTC, ETH a SOL", cta: "Zobraziť celú Liquidity Map", basis: "Na základe dát z 27 búrz · DeFi · Veľryby", updated: "aktualizované pred", loading_short: "načítavam…", no_data: "Bez dát" },
  de: { subtitle: "Globaler Liquidations-Druckindex · BTC, ETH und SOL", cta: "Vollständige Liquiditätskarte ansehen", basis: "Basierend auf Daten von 27 Börsen · DeFi · Wale", updated: "aktualisiert vor", loading_short: "lade…", no_data: "Keine Daten" },
  "zh-CN": { subtitle: "全球强制平仓压力指数 · BTC、ETH 和 SOL", cta: "查看完整流动性地图", basis: "基于 27 家交易所的数据 · DeFi · 巨鲸", updated: "更新于", loading_short: "加载中…", no_data: "无数据" },
};

const scoreBand = (s, L = BAND_LABELS.es) => {
  if (s >= 90) return { color: "#ffffff", label: L.cascade, emoji: "⚫" };
  if (s >= 75) return { color: "#ff3b5c", label: L.crit,    emoji: "🔴" };
  if (s >= 50) return { color: "#ff5a00", label: L.high,    emoji: "🟠" };
  if (s >= 25) return { color: "#f0c800", label: L.mod,     emoji: "🟡" };
  return                { color: "#00d084", label: L.low,     emoji: "🟢" };
};

const fmtSeconds = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`);

// fetchWithTimeout — aborts after FETCH_TIMEOUT_MS so a stalled endpoint
// can flip the UI from "loading" to "Sin datos" instead of hanging on "—".
const fetchScore = async (coin, signal) => {
  try {
    const r = await fetch(`${API}/liqmap/liqscore/${coin}`, { signal });
    if (!r.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[LiqScoreHome] /liqscore/${coin} → HTTP ${r.status}`);
      return null;
    }
    const d = await r.json();
    if (typeof d?.score !== "number") {
      // eslint-disable-next-line no-console
      console.warn(`[LiqScoreHome] /liqscore/${coin} → invalid payload`, d);
      return null;
    }
    return d;
  } catch (e) {
    if (e?.name !== "AbortError") {
      // eslint-disable-next-line no-console
      console.warn(`[LiqScoreHome] /liqscore/${coin} fetch error:`, e?.message || e);
    }
    return null;
  }
};

export default function LiqScoreHomeCard() {
  const [data, setData] = useState({}); // { BTC: payload, ETH: payload, SOL: payload }
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;

    const load = async () => {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
      try {
        const responses = await Promise.all(
          COINS.map((c) => fetchScore(c, ctl.signal))
        );
        if (!alive) return;
        const next = {};
        responses.forEach((d, i) => { if (d) next[COINS[i]] = d; });
        setData(next);
        // If we got at least one valid coin, clear the error state.
        const anyOk = Object.keys(next).length > 0;
        setErrored(!anyOk);
      } finally {
        clearTimeout(timer);
        if (alive) setLoading(false);
      }
    };

    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { lang } = useLanguage();
  const bandTxt = BAND_LABELS[lang] || BAND_LABELS.en || BAND_LABELS.es;
  const cardTxt = CARD_TXT[lang] || CARD_TXT.en || CARD_TXT.es;
  const btc = data.BTC;
  const score = Number(btc?.score) || 0;
  const band = scoreBand(score, bandTxt);
  const ts = (btc?.timestamp || 0) * 1000;
  const ago = ts ? Math.max(0, Math.floor((now - ts) / 1000)) : 0;

  // ─── Loading placeholder used until ANY data arrives. Avoids the
  // "—" flash that the user reported on production. ───
  const LoadingDot = (
    <Loader2 className="inline h-3 w-3 animate-spin text-white/40" />
  );

  // ─── Error placeholder shown when all 3 fetches failed/timed out. ───
  const NoData = (
    <span className="font-mono text-[11px] text-white/35">{cardTxt.no_data}</span>
  );

  return (
    <section className="flex justify-center px-4 pt-4 pb-8 sm:pt-4 sm:pb-10">
      <div
        data-testid="liqscore-home-card"
        className="w-full max-w-[420px] rounded-2xl p-5 sm:p-6"
        style={{
          background: "rgba(249,115,22,0.05)",
          border: "1px solid rgba(249,115,22,0.18)",
          boxShadow: "0 12px 32px -16px rgba(249,115,22,0.25)",
        }}
      >
        <p
          className="font-mono font-bold uppercase"
          style={{ color: "#f97316", fontSize: 11, letterSpacing: "3px" }}
          data-testid="liqscore-home-title"
        >
          ⚡ RE₿EL LiqScore™
        </p>
        <p className="mt-1 text-[11px] text-white/55">
          {cardTxt.subtitle}
        </p>

        {/* Protagonist (BTC) */}
        <div className="mt-5 flex items-baseline gap-3">
          <span
            data-testid="liqscore-home-btc-score"
            className="font-mono"
            style={{
              fontSize: 48,
              fontWeight: 900,
              color: btc ? band.color : "rgba(255,255,255,0.4)",
              lineHeight: 1,
            }}
          >
            {btc ? score : (loading ? LoadingDot : (errored ? NoData : LoadingDot))}
          </span>
          <span className="font-mono text-white/40" style={{ fontSize: 18 }}>/100</span>
        </div>
        <p
          className="mt-1 font-mono font-bold"
          style={{ fontSize: 13, color: btc ? band.color : "rgba(255,255,255,0.4)" }}
          data-testid="liqscore-home-btc-level"
        >
          {btc
            ? <>{band.emoji} BTC · {band.label}</>
            : (loading
              ? <>BTC · {cardTxt.loading_short}</>
              : <>BTC · {cardTxt.no_data}</>
            )}
        </p>

        {/* Big progress bar */}
        <div
          className="mt-3 h-2 w-full overflow-hidden rounded-full"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <div
            className="h-full transition-all duration-700 ease-out"
            style={{
              width: btc ? `${Math.max(0, Math.min(100, score))}%` : "0%",
              background: `linear-gradient(90deg, ${band.color}88, ${band.color})`,
            }}
          />
        </div>

        {/* Compact rows for ETH + SOL (and BTC summary row) */}
        <div className="mt-5 space-y-2" data-testid="liqscore-home-rows">
          {COINS.map((c) => {
            const d = data[c];
            const s = Number(d?.score) || 0;
            const b = scoreBand(s, bandTxt);
            return (
              <div key={c} className="flex items-center gap-3 font-mono text-[11px]" data-testid={`liqscore-home-row-${c}`}>
                <span className="w-8 font-bold text-white/85">{c}</span>
                <span className="w-10 text-right font-black" style={{ color: d ? b.color : "rgba(255,255,255,0.35)", fontSize: 13 }}>
                  {d ? s : (loading ? "…" : "—")}
                </span>
                <div className="h-[6px] flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div
                    className="h-full transition-all duration-700 ease-out"
                    style={{ width: d ? `${Math.max(0, Math.min(100, s))}%` : "0%", background: b.color }}
                  />
                </div>
                <span style={{ color: d ? b.color : "rgba(255,255,255,0.25)" }}>
                  {d ? b.emoji : "·"}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <Link
          to="/liquidity-map"
          data-testid="liqscore-home-cta"
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider text-black transition-all hover:opacity-90"
          style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
        >
          {cardTxt.cta}
          <ArrowRight size={14} />
        </Link>

        <p className="mt-3 font-mono text-white/35" style={{ fontSize: 9 }}>
          {cardTxt.basis}
          {btc ? ` · ${cardTxt.updated} ${fmtSeconds(ago)}` : (loading ? ` · ${cardTxt.loading_short}` : "")}
        </p>
      </div>
    </section>
  );
}
